import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  formatTagListForInput,
  normalizeStringArray,
  parseTagListInput,
} from '../utils/compatibilityOptions';

const FieldStack = styled.div`
  display: grid;
  gap: 10px;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: ${(props) => (props.disabled ? '#f8fafc' : '#ffffff')};
  color: #374151;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const HelperText = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
`;

const SuggestedTagSection = styled.div`
  display: grid;
  gap: 8px;
`;

const SuggestedTagLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
`;

const SuggestedTagGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SuggestedTagCard = styled.button`
  border: 1px solid ${(props) => (props.$active ? '#2563eb' : '#dbe4f0')};
  background: ${(props) => (props.$active ? '#dbeafe' : '#f8fafc')};
  color: ${(props) => (props.$active ? '#1d4ed8' : '#334155')};
  border-radius: 999px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    background: #eff6ff;
    color: #1d4ed8;
  }
`;

const buildNextTagList = (currentValue, tag) => {
  const normalizedTag = String(tag || '').trim();
  const currentTags = parseTagListInput(currentValue);

  if (!normalizedTag || currentTags.includes(normalizedTag)) {
    return {
      tags: currentTags,
      inputValue: String(currentValue || ''),
    };
  }

  const nextTags = [...currentTags, normalizedTag];

  return {
    tags: nextTags,
    inputValue: nextTags.join(', '),
  };
};

const CapabilityTagsField = ({
  value,
  onChange,
  suggestedTags = [],
  disabled = false,
  placeholder = '',
  helperText = 'Type free-form tags, or click a suggested tag to insert it.',
  inputAriaLabel,
  className,
  name,
}) => {
  const normalizedValue = useMemo(() => formatTagListForInput(value), [value]);
  const normalizedSuggestedTags = useMemo(
    () => normalizeStringArray(suggestedTags),
    [suggestedTags]
  );
  const [draftValue, setDraftValue] = useState(normalizedValue);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (!isEditingRef.current) {
      setDraftValue(normalizedValue);
    }
  }, [normalizedValue]);

  const activeTags = useMemo(() => parseTagListInput(draftValue), [draftValue]);

  const handleInputChange = (event) => {
    const nextInputValue = event.target.value;
    const nextTags = parseTagListInput(nextInputValue);

    setDraftValue(nextInputValue);
    onChange?.(nextTags, nextInputValue);
  };

  const handleSuggestedTagClick = (tag) => {
    if (disabled) {
      return;
    }

    const nextValue = buildNextTagList(draftValue, tag);
    setDraftValue(nextValue.inputValue);
    onChange?.(nextValue.tags, nextValue.inputValue);
  };

  return (
    <FieldStack className={className}>
      <FieldInput
        type="text"
        name={name}
        value={draftValue}
        onChange={handleInputChange}
        onFocus={() => {
          isEditingRef.current = true;
        }}
        onBlur={() => {
          isEditingRef.current = false;
        }}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={inputAriaLabel}
      />

      {helperText ? <HelperText>{helperText}</HelperText> : null}

      {normalizedSuggestedTags.length > 0 && (
        <SuggestedTagSection>
          <SuggestedTagLabel>Suggested tags</SuggestedTagLabel>
          <SuggestedTagGrid>
            {normalizedSuggestedTags.map((tag) => (
              <SuggestedTagCard
                key={tag}
                type="button"
                onClick={() => handleSuggestedTagClick(tag)}
                disabled={disabled}
                $active={activeTags.includes(tag)}
                title={activeTags.includes(tag) ? `${tag} already added` : `Insert ${tag}`}
                aria-pressed={activeTags.includes(tag)}
              >
                {tag}
              </SuggestedTagCard>
            ))}
          </SuggestedTagGrid>
        </SuggestedTagSection>
      )}
    </FieldStack>
  );
};

export default CapabilityTagsField;
